import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchJackpotWinStats } from '../../../core/api/endpoints/jackpotWins.api';
import { useAppStore } from '../../../store/app/appStore';
import { selectMockJackpotWins, useJackpotStatisticsStore } from '../store/jackpotStatisticsStore';
import type { IJackpotWinsData } from '../types/jackpotStatistics.types';

export const JACKPOT_WINS_QUERY_KEY = ['JackpotWins'];

/**
 * How long a fetched win list is considered fresh.
 *
 */
const STALE_TIME_MS = 60_000;

/**
 * Returned when the request failed or the query is disabled. Frozen and module-level so the
 * drawer's memoised selectors keep a stable reference between renders.
 */
const EMPTY_JACKPOT_WINS: IJackpotWinsData = Object.freeze({ levels: [] });

export interface JackpotWinsResult {
  /** The win rows — an empty payload until the first response lands, never `undefined`. */
  data: IJackpotWinsData;
  isLoaded: boolean;
}

/**
 * Win rows for one jackpot instance, grouped per level, from `/api/jackpot/win/stats`.
 *
 * Only mounted while the drawer is open — `JackpotStatisticsHost` unmounts the whole subtree on
 * close — so the query needs no explicit open/closed gate.
 *
 * @param jackpotName the instance name as the jackpots store spells it (e.g. 'Bell Link').
 */
export const useJackpotWins = (jackpotName: string): JackpotWinsResult => {
  const apiHost = useAppStore((state) => state.settings.apiHost);
  // QA-only override (Jackpot Mock tab → "Statistics mock"). Selecting the resolved payload keeps
  // the reference stable, so the drawer's memoised selectors do not recompute on every render.
  const mockWins = useJackpotStatisticsStore((state) => selectMockJackpotWins(state, jackpotName));

  const enabled = Boolean(apiHost) && Boolean(jackpotName) && mockWins === null;
  const { data, isError } = useQuery<IJackpotWinsData, Error>({
    queryKey: [...JACKPOT_WINS_QUERY_KEY, jackpotName],
    queryFn: () => fetchJackpotWinStats(jackpotName),
    // `mockWins !== null` means QA has taken this endpoint over. The real query must be DISABLED
    // then, not merely ignored — same gate as `useCampaignsQuery`'s `!mockMode` — otherwise a
    // mocked (or synthetic) instance name 404-spams the API for as long as the drawer is open.
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    // One retry for a transient network blip; the drawer is short-lived, so a longer retry chain
    // would resolve after the player has closed it.
    retry: 1,
  });

  // Memoised so the result object keeps a stable reference between renders — the drawer's
  // selectors are memoised on it.
  return useMemo(() => {
    if (mockWins !== null) return { data: mockWins, isLoaded: true };
    if (data) return { data, isLoaded: true };

    // A disabled query never settles (react-query keeps it pending), so `enabled` decides that
    // case rather than any query flag.
    return { data: EMPTY_JACKPOT_WINS, isLoaded: isError || !enabled };
  }, [mockWins, data, isError, enabled]);
};
